//
// channel-manager.js
//

const configurators = document.querySelectorAll('[data-channel-manager]');
const billingToggle = document.querySelector('[data-billing-toggle]');
const planPrices = document.querySelectorAll('[data-plan-price]');
const configuratorUpdates = [];

const YEARLY_DISCOUNT = 0.2;
const currentLanguage = window.okupaiI18n?.language || 'en';
const translate = (value) => window.okupaiI18n?.t(value) || value;

const PLAN_TIERS = {
  basic: [
    { from: 1, to: 20, price: 12 },
    { from: 21, to: 2000, price: 10 },
  ],
  advanced: [
    { from: 1, to: 19, price: 25 },
    { from: 20, to: 29, price: 24 },
    { from: 30, to: 39, price: 23 },
    { from: 40, to: 49, price: 22 },
    { from: 50, to: 59, price: 21 },
    { from: 60, to: 69, price: 20 },
    { from: 70, to: 79, price: 19 },
    { from: 80, to: 89, price: 18 },
    { from: 90, to: 99, price: 17 },
    { from: 100, to: 2000, price: 12 },
  ],
  pro: [
    { from: 1, to: 9, price: 75 },
    { from: 10, to: 19, price: 65 },
    { from: 20, to: 29, price: 54 },
    { from: 30, to: 39, price: 43 },
    { from: 40, to: 49, price: 37 },
    { from: 50, to: 59, price: 31 },
    { from: 60, to: 69, price: 30 },
    { from: 70, to: 79, price: 29 },
    { from: 80, to: 89, price: 28 },
    { from: 90, to: 99, price: 27 },
    { from: 100, to: 2000, price: 22 },
  ],
};

const formatter = new Intl.NumberFormat(currentLanguage === 'de' ? 'de-DE' : 'en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const compactFormatter = new Intl.NumberFormat(currentLanguage === 'de' ? 'de-DE' : 'en-US', {
  maximumFractionDigits: 2,
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const formatPlanPrice = (value) =>
  (Number.isInteger(value) ? compactFormatter : formatter).format(value);
const isYearlyBilling = () => Boolean(billingToggle?.checked);
const getBillingMultiplier = () => (isYearlyBilling() ? 1 - YEARLY_DISCOUNT : 1);

const getPriceForUnit = (plan, unitCount) => {
  const tiers = PLAN_TIERS[plan] || PLAN_TIERS.advanced;
  const tier = tiers.find(({ from, to }) => unitCount >= from && unitCount <= to);

  return tier ? tier.price : tiers[tiers.length - 1].price;
};

const calculatePrice = (plan, units) => {
  let total = 0;

  for (let unit = 1; unit <= units; unit += 1) {
    total += getPriceForUnit(plan, unit);
  }

  return {
    nextUnitPrice: getPriceForUnit(plan, units + 1),
    averageUnitPrice: total / units,
    total,
  };
};

configurators.forEach((configurator) => {
  const plan = configurator.querySelector('[data-channel-manager-plan]');
  const range = configurator.querySelector('[data-channel-manager-range]');
  const input = configurator.querySelector('[data-channel-manager-input]');
  const nextUnitPrice = configurator.querySelector('[data-channel-manager-next-unit-price]');
  const total = configurator.querySelector('[data-channel-manager-total]');
  const average = configurator.querySelector('[data-channel-manager-average]');
  const billingNote = configurator.querySelector('[data-channel-manager-billing-note]');

  if (!plan || !range || !input || !nextUnitPrice || !total || !average) {
    return;
  }

  const min = Number(range.min);
  const max = Number(range.max);

  const render = (units, syncInput = true) => {
    const price = calculatePrice(plan.value, units);
    const billingMultiplier = getBillingMultiplier();
    const displayedNextUnitPrice = price.nextUnitPrice * billingMultiplier;
    const displayedTotal = price.total * billingMultiplier;
    const displayedAverage = price.averageUnitPrice * billingMultiplier;

    range.value = units;
    if (syncInput) {
      input.value = units;
    }
    nextUnitPrice.textContent = formatter.format(displayedNextUnitPrice);
    total.textContent = formatter.format(displayedTotal);
    average.textContent = formatter.format(displayedAverage);

    if (billingNote) {
      billingNote.textContent = isYearlyBilling()
        ? currentLanguage === 'de'
          ? `20 % Jahresrabatt angewendet. ${formatter.format(displayedTotal * 12)} € gesamt für ${units} Einheiten werden jährlich abgerechnet.`
          : `20% yearly discount applied. €${formatter.format(displayedTotal * 12)} total for ${units} units billed yearly.`
        : translate('Total and per-unit prices shown per month, billed monthly.');
    }
  };

  const normalizeUnits = (value) => clamp(Number.parseInt(value, 10) || min, min, max);

  const update = (value, syncInput = true) => {
    const units = normalizeUnits(value);

    render(units, syncInput);
  };

  range.addEventListener('input', () => update(range.value));
  input.addEventListener('input', () => {
    const sanitizedValue = input.value.replace(/\D/g, '');

    if (sanitizedValue !== input.value) {
      input.value = sanitizedValue;
    }

    if (sanitizedValue === '') {
      return;
    }

    update(sanitizedValue, false);
  });
  input.addEventListener('change', () => update(input.value));
  input.addEventListener('blur', () => update(input.value));
  plan.addEventListener('change', () => update(range.value));

  configuratorUpdates.push(() => update(range.value));
  update(range.value);
});

const renderBillingCycle = () => {
  const billingMultiplier = getBillingMultiplier();

  planPrices.forEach((priceElement) => {
    const monthlyPrice = Number(priceElement.dataset.monthlyPrice);
    const displayedPrice = monthlyPrice * billingMultiplier;
    const billingNote = priceElement.closest('.card-body')?.querySelector('[data-plan-billing-note]');

    priceElement.textContent = formatPlanPrice(displayedPrice);

    if (billingNote) {
      billingNote.textContent = isYearlyBilling()
        ? currentLanguage === 'de'
          ? `Monatlicher Gegenwert – ${formatter.format(displayedPrice * 12)} € pro Einheit, jährlich abgerechnet`
          : `Monthly equivalent — €${formatter.format(displayedPrice * 12)} per unit, billed yearly`
        : translate('Per unit, billed monthly');
    }
  });

  configuratorUpdates.forEach((update) => update());
};

billingToggle?.addEventListener('change', renderBillingCycle);
renderBillingCycle();
